/* global console setTimeout */
import React from "react";
import ReactClass from "create-react-class";
import * as _ from "lodash";
import debounce from "lodash/debounce";

import PropertyModel from "./PropertyModel";
import AI from "../../appInsights";

export default ReactClass({
  displayName: "StatsPanel",
  
  componentWillMount() {
    // Create debounced version of the actual filtering logic
    this.debouncedApplyFilter = debounce(this.applyFilter, 300);
  },
  
  componentWillUnmount() {
    // Cancel any pending debounced calls
    if (this.debouncedApplyFilter) {
      this.debouncedApplyFilter.cancel();
    }
  },
  
  getInitialState() {
    return {
      originalPropGroups: [],
      propGroups: [],
      statsFilter: "",
      record: null,
      loading: true
    };
  },
  componentWillReceiveProps(nextProps) {
    const {type, mode, record, keyMap, showProperties, loading} = nextProps;

    if (type && type !== this.state.applianceType) {
      this.resetProperties(type, mode);
    } else if (showProperties && showProperties.length && !this.state.showProperties) {
      this.setState({showProperties});
      this.resetProperties(type, mode, showProperties);
    }
    
    // Apply existing filter if there is one (without delay)
    if (this.state.statsFilter !== "") {
      this.debouncedApplyFilter(this.state.statsFilter);
    }

    this.setState({record, loading});

    if (keyMap) {
      this.setState({keyMap});
    }
  },

  resetProperties(type, mode, showProperties = undefined) {
    PropertyModel.forModel(type, mode).then(propModel => {
      if (!propModel) {
        return;
      }

      this.propertyModel = new PropertyModel(propModel);
      if (showProperties && showProperties.length) {
        this.propertyModel.keep(showProperties);
      }

      const groups = _.clone(this.propertyModel.byTypeGrouped("Temp", "RPM", "Numeric", "Number", "Bool", "Enum", "EnumLine", "MixedEnumNumber"));
      //Removing color from appliance config file to avoid setting selected color in stats panel
      const selectedFieldsArray = groups.map((obj) => {
          return {
            group: obj.group,
            properties: obj.properties
          }
        }
      );
      this.setState({
        originalPropGroups: groups,
        propGroups: groups,
        applianceType: type
      });
    }, errResponse => {
    });
  },

  togglePropCollapse(entry, index, selectedAttributesLength) {
    const before = this.state.propGroups.slice(0, index),
        after = this.state.propGroups.slice(index + 1)
    ;
    this.setState({
      propGroups: [
        ...before,
        Object.assign(entry, {
          collapsed: ( entry.collapsed === undefined
            ? selectedAttributesLength === 0
              ? false
              : true
            : !entry.collapsed )
        }),
        ...after
      ]
    });
  },

  expandAll() {
    this.setState({
      propGroups: this.state.propGroups.map(
        entry => Object.assign(entry, {collapsed: false})
      )
    });
  },
  collapseAll() {
    this.setState({
      propGroups: this.state.propGroups.map(
        entry => Object.assign(entry, {collapsed: true})
      )
    });
  },

  filterStats(filterStr) {
    // Immediately update the input value for responsive UI
    this.setState({ statsFilter: filterStr });
    
    // Debounce the actual filtering logic
    this.debouncedApplyFilter(filterStr);
  },
  
  applyFilter(filterStr) {
    const origProps = this.state.originalPropGroups;
    
    if (filterStr !== "") {
      AI.trackEvent("GraphStatsPanelFilter", {"Appliance Type": this.state.applianceType, "Filter String": filterStr});
    }

    this.expandAll();

    // Apply filtering without setTimeout - make it synchronous
    this.setState({
      propGroups: (filterStr.trim().length > 1
        ? _.cloneDeep(origProps).map(group => {
            group.properties = group.properties
              .filter(prop =>
                prop.displayName.toLowerCase().indexOf(filterStr.toLowerCase()) !== -1
              );
            return group;
          })
        : origProps)
    });
  },

  clearAll() {
    const {onSelectionChange} = this.props;
    onSelectionChange && onSelectionChange([])
  },

  // NEW: Helper methods for dependency checking
  findDependentProperty(prop) {
    if (!prop.dependentField) return null;
    
    // Search through all property groups to find the dependent property
    for (let group of this.state.propGroups) {
      const found = group.properties.find(p => 
        p.name === prop.dependentField || p.valueField === prop.dependentField
      );
      if (found) return found;
    }
    return null;
  },

  canDeselectProperty(prop, currentSelection) {
    // Check if this property is required by any currently selected property
    const dependentProps = currentSelection.filter(selected => 
      selected.dependentField === prop.name
    );
    
    // Debug logging
    if (dependentProps.length > 0) {
      console.log(`Property "${prop.name}" (${prop.displayName}) is required by:`, dependentProps.map(p => p.displayName));
    }
    
    return dependentProps.length === 0;
  },

  showDependencyWarning(prop, requiredBy) {
    const requiredByNames = requiredBy.map(p => p.displayName).join(', ');
    alert(`Cannot remove "${prop.displayName}" - it is required by: ${requiredByNames}`);
  },

  getDependentFields(prop, allProps) {
    // Find all properties that depend on this one
    return allProps.filter(p => p.dependentField === prop.name);
  },

  getEnhancedTooltip(prop, currentSelection) {
    // PRESERVE: Original tooltip format first
    let tooltip = prop.displayName;
    if (prop.description) {
      tooltip += `\r\n${prop.description}\n`;
    }
    if (prop.valuesDescription) {
      tooltip += `\r\n${prop.valuesDescription}`;
    }
    
    // ADD: New dependency information
    const allProps = this.state.propGroups.flatMap(group => group.properties);
    const requiredBy = currentSelection.filter(selected => 
      selected.dependentField === prop.name
    );
    
    if (requiredBy.length > 0) {
      const requiredByNames = requiredBy.map(p => p.displayName).join(', ');
      tooltip += `\r\n\r\n🔗 Required by: ${requiredByNames}`;
      tooltip += `\r\nCannot be removed while required.`;
    }
    
    if (prop.dependentField) {
      const dependentProp = this.findDependentProperty(prop);
      const dependentName = dependentProp?.displayName || prop.dependentField;
      tooltip += `\r\n\r\n→ Selecting this will also select: ${dependentName}`;
    }
    
    return tooltip;
  },

  handlePropertyClick(prop, currentSelection) {
    const isSelected = currentSelection.some(field => field.id === prop.id);
    
    if (!isSelected) {
      // SELECTION: Add the selected property
      let newSelection = [...currentSelection, { ...prop, selected: true }];
      
      // AUTO-SELECT dependent fields if this property has dependentField
      if (prop.dependentField) {
        const allProps = this.state.propGroups.flatMap(group => group.properties);
        const dependentProp = allProps.find(p => p.name === prop.dependentField);
        
        if (dependentProp && !newSelection.some(s => s.id === dependentProp.id)) {
          console.log(`Auto-selecting dependent property: ${dependentProp.displayName} for ${prop.displayName}`);
          newSelection.push({ ...dependentProp, selected: true, autoSelected: true });
        }
      }
      
      this.props.onSelectionChange && this.props.onSelectionChange(newSelection);
    } else {
      // DESELECTION: Check if this property is required by others
      const requiredBy = currentSelection.filter(selected => 
        selected.dependentField === prop.name
      );
      
      if (requiredBy.length > 0) {
        // Block deselection and show warning
        this.showDependencyWarning(prop, requiredBy);
        return;
      }
      
      // Allow deselection and auto-deselect dependent fields
      let newSelection = currentSelection.filter(field => field.id !== prop.id);
      
      // AUTO-DESELECT dependent fields if this property was controlling them
      if (prop.dependentField) {
        // Remove auto-selected dependent fields when the controlling field is deselected
        newSelection = newSelection.filter(field => 
          !(field.autoSelected && field.name === prop.dependentField)
        );
        console.log(`Auto-deselecting dependent property for ${prop.displayName}`);
      }
      
      this.props.onSelectionChange && this.props.onSelectionChange(newSelection);
    }
  },

  handleGroupClick(entry, isGroupSelected, fieldSelection) {
    if (isGroupSelected) {
      // DESELECTION: Check if any properties in this group are required
      const conflictingProps = [];
      const allProps = this.state.propGroups.flatMap(group => group.properties);
      
      entry.properties.forEach(prop => {
        const requiredBy = fieldSelection.filter(selected => 
          selected.dependentField === prop.name
        );
        if (requiredBy.length > 0) {
          conflictingProps.push({prop, requiredBy});
        }
      });
      
      if (conflictingProps.length > 0) {
        const messages = conflictingProps.map(({prop, requiredBy}) => 
          `${prop.displayName} (required by: ${requiredBy.map(p => p.displayName).join(', ')})`
        );
        alert(`Cannot remove the following properties:\n${messages.join('\n')}`);
        return;
      }
      
      // Safe to deselect all
      const filterList = fieldSelection.filter(selected => 
        !entry.properties.some(prop => selected.id === prop.id)
      );
      this.props.onSelectionChange && this.props.onSelectionChange([...filterList]);
    } else {
      // SELECTION: Always allow
      const uniqList = _.uniqBy([...fieldSelection, ...(entry.properties.map(p => { 
        p.selected = true; 
        return p 
      }))], function (p) {
        return p.id;
      });
      this.props.onSelectionChange && this.props.onSelectionChange([...uniqList]);
    }
  },
  render: function() {
    const {props: {fieldSelection, onSelectionChange, busy, isLhsPanelOpen, toggleSidePanel}, state: {record, keyMap, loading, propGroups}} = this,
        displayValue = (record, prop, keyMap) => {
          const value = record[keyMap[prop.name]];
          switch(prop.type) {
            case "Enum":
            case "EnumLine":
            case "MixedEnumNumber":
              // NEW: Try dependent mapping first, fallback to existing logic
              if (prop.dependentField && keyMap[prop.dependentField]) {
                try {
                  const dependentValue = record[keyMap[prop.dependentField]];
                  return prop.valueMapping(value, dependentValue) || value;
                } catch (error) {
                  // Fallback to original mapping if dependent mapping fails
                  return (prop.originalValueMapping ? prop.originalValueMapping(value) : value) || value;
                }
              }
              // EXISTING logic unchanged
              return prop.valueMapping(value) || value;
            case "Bool":
              // NEW: Try dependent mapping first, fallback to existing logic  
              if (prop.dependentField && keyMap[prop.dependentField]) {
                try {
                  const dependentValue = record[keyMap[prop.dependentField]];
                  return prop.valueMapping(value, dependentValue) || value;
                } catch (error) {
                  // Fallback to original mapping if dependent mapping fails
                  return (prop.originalValueMapping ? prop.originalValueMapping(value) : value) || value;
                }
              }
              // EXISTING logic unchanged
              return prop.valueMapping(value) || value;
            default:
              // EXISTING logic unchanged
              return value;
          }
        },
        iconClass = (record, prop, keyMap) => {
          const value = record[keyMap[prop.name]];
          const iconValueMapping = {
            Bool: {
              "Yes": `icon-graph-onoff on`,
              "No": `icon-graph-onoff off`,
              "On": `icon-graph-onoff on`,
              "Off": `icon-graph-onoff off`,
              "Invalid": `icon-graph-onoff invalid`,
              "Active": `icon-graph-active`,
              "Not Active": `icon-graph-inactive`,
              "Inactive": `icon-graph-inactive`
            },
            Enum: {
              "High": "icon-graph-high",
              "Medium": "icon-graph-medium",
              "Low": "icon-graph-low",
              "No Feedback": "icon-graph-nofeedback"
            }
          };
          /*
          .icon-graph-error
          .icon-graph-info
          .icon-graph-droplet
          .icon-graph-maximize
          .icon-graph-minimize
          */
          switch(prop.type) {
            case "Enum":
            case "EnumLine":
            case "MixedEnumNumber":
            case "Bool":
              return iconValueMapping.Bool[prop.valueMapping(value)] ||
                iconValueMapping.Enum[prop.valueMapping(value)];
            default:
              return undefined;
          }
        };

    const empty = !loading && propGroups && propGroups.length === 0;
    return (
      <div>
        <div id="divStatsPanel" className="stats-panel">
          <div className="header">
            <label id="lblAddZoneEle">Add Zone Elements</label>
            <i className={`icon icon-chevron-left`} onClick={() => {
              toggleSidePanel && toggleSidePanel();
            }}></i>
          </div>
          <ul className="actions">
            <li>
              <input type="text"
                     placeholder="Filter Attributes"
                     value={this.state.statsFilter}
                     disabled={empty || loading}
                     onChange={e => this.filterStats(e.target.value)}
              />
              <i className="icon-graph-expand-all" title="Expand All"
                 disabled={empty || loading}
                 onClick={this.expandAll} />
              <i className="icon-graph-collapse-all" title="Collapse All"
                 disabled={empty || loading}
                 onClick={this.collapseAll} />
              <i id="aClearAll" className="icon-slash" 
                  onClick={this.clearAll}
                  title="Clear All"
                  disabled={empty || loading}/>
            </li>
          </ul>
          {!empty ? propGroups.map((entry, index) => {
            const filterSelectedProps = fieldSelection.filter(selected => entry.properties.some(prop => selected.id === prop.id )),
              filterLength = filterSelectedProps.length;
            const isGroupSelected = entry.properties.length > 0
              ? filterLength === entry.properties.length
              : false;

            return (<ul key={index} className={ `${ entry.collapsed === undefined
                    ? filterLength === 0
                      ? "collapsed"
                      : ""
                    : entry.collapsed
                      ? "collapsed"
                      : "" }` } >
                <li className="heading">
                  <label>
                    <div className={`chkBox ${busy ? "disabled" : ""}`} onClick={(e) => {
                      if(!busy) {
                        this.handleGroupClick(entry, isGroupSelected, fieldSelection);
                      }
                    }}>
                      <i className={"icon-check " + (isGroupSelected ? "checked" : "unchecked")} />
                      <span>{entry.group}</span>
                    </div>
                  </label>
                  <i className="icon" onClick={() => this.togglePropCollapse(entry, index, filterLength)} >
                    { entry.collapsed === undefined && filterLength === 0
                      ? "+"
                      : entry.collapsed
                        ? "+"
                        : "-" }
                  </i>
                </li>
                <ul className="props-list">
                  {entry.properties.map((prop, index) => {
                    const isSelected = fieldSelection.filter(field => field.id === prop.id).length > 0;
                    //Fetching color from selected field
                    const selectedPropertyColor = isSelected ? fieldSelection.filter(field => field.id === prop.id) : {};
                    const canDeselect = this.canDeselectProperty(prop, fieldSelection);
                    const enhancedTooltip = this.getEnhancedTooltip(prop, fieldSelection);
                    
                    return (<li key={index} className={`${isSelected ? "selected" : ""} ${busy ? "disabled" : ""} ${!canDeselect && isSelected ? "locked" : ""}`} onClick={() => {
                      if (!busy) {
                        this.handlePropertyClick(prop, fieldSelection);
                      }
                    }}>
                      <label title={enhancedTooltip} style={isSelected ? { "color": selectedPropertyColor[0].color } : {}}>
                        {prop.displayName}
                        {isSelected && !canDeselect && <span style={{color: '#17a2b8'}}> 🔗</span>}
                      </label>
                      <label title={record && keyMap && isSelected ? displayValue(record, prop, keyMap) : "-"}>
                        {record && keyMap && isSelected && (<i className={iconClass(record, prop, keyMap)} />)}
                        {record && keyMap && isSelected ? displayValue(record, prop, keyMap) : "-"}
                      </label>
                    </li>)
                  })}
                </ul>
              </ul>)
          }) : (
            <div className="stats-empty-indicator">
              <h1>No attribute data available</h1>
            </div>
          )}
        </div>
        {loading === true && (
          <div className="stats-loading-indicator">
            <h1>LOADING...</h1>
          </div>
        )}
        {!isLhsPanelOpen && (
          <i className={`icon icon-chevron-right ico-expand`} onClick={() => {
            toggleSidePanel && toggleSidePanel();
          }}></i>
        )}
      </div>
    );
  }
});
